import * as React from 'react';
import { useSelector } from 'react-redux';
import { MortgageBannersLayout } from 'shared/components/MortgageBannersLayout';
import { MortgageWidgetPromoDiscount } from 'shared/components/MortgageWidgetPromoDiscount';
import { NewbuildingMortgageWidgetPromoDiscount } from 'shared/components/NewbuildingMortgageWidgetPromoDiscount';
import { selectAsideCompactMode } from 'shared/selectors/aside';
import { IState } from 'shared/store';

import {
  abUseExperimentsSelector,
  isAbUseExperimentsActive,
  isOfferInGageSelector,
  offerSelector,
  selectIsAgent,
} from '../../selectors';
import { REPLACEMENT_BUTTON_UNDER_PRICE_EXPERIMENT } from '../../services/abUseExperiments/experiments';
import { getOfferType, isNewbuilding, isOldbuilding, isSuburban } from '../../utils/offer_type';
import { getMortgageBannerVerticalName } from '../../utils/tracking/mortgageUnderPriceCianBanner';
import { useApplicationContext } from '../ApplicationContext';
import { SubsidisedMortgageBannerContainer } from '../SubsidisedMortgageBannerContainer';

export const MortgageWidgetContainer: React.FC = () => {
  const { config } = useApplicationContext();
  const offer = useSelector(offerSelector);
  const isInGage = useSelector(isOfferInGageSelector);
  const abUseExperiments = useSelector(abUseExperimentsSelector);
  const isCompactModeEnabled = useSelector(selectAsideCompactMode);
  const mortgagePromoDesign = useSelector((state: IState) => state.mortgagePromoDesign);
  const isAgent = useSelector(selectIsAgent);
  const isMortgageDisallowed = 'mortgageAllowed' in offer.bargainTerms && offer.bargainTerms.mortgageAllowed === false;

  const isEnabled = config.get<boolean>('mortgage.isWidgetEnabled') || false;
  const offerType = getOfferType(offer);
  const mortgageBannerVerticalName = getMortgageBannerVerticalName(offer);
  const isExperimentGroupA = isAbUseExperimentsActive(abUseExperiments, REPLACEMENT_BUTTON_UNDER_PRICE_EXPERIMENT, 'A');
  const promoVariant = isExperimentGroupA ? 'new' : 'old';

  const promoDiscountBanner = (
    <MortgageWidgetPromoDiscount
      mortgagePromoDesign={mortgagePromoDesign}
      verticalName={mortgageBannerVerticalName}
      variant={promoVariant}
    />
  );

  if (!isEnabled || isInGage || isCompactModeEnabled || isMortgageDisallowed) {
    return null;
  }

  if (isNewbuilding(offerType)) {
    return isExperimentGroupA ? (
      promoDiscountBanner
    ) : (
      <NewbuildingMortgageWidgetPromoDiscount
        mortgagePromoDesign={mortgagePromoDesign}
        verticalName={mortgageBannerVerticalName}
      />
    );
  }

  if (isSuburban(offerType)) {
    return promoDiscountBanner;
  }

  if (isOldbuilding(offerType)) {
    return (
      <MortgageBannersLayout>
        <SubsidisedMortgageBannerContainer />
        {!isAgent && promoDiscountBanner}
      </MortgageBannersLayout>
    );
  }

  return promoDiscountBanner;
};
